import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryClient';
import { Asset } from '@/types';
import {
  fetchAssets,
  fetchAsset,
  fetchAssetTypes,
  fetchAssetStatuses,
  fetchAssetSummary,
  createAsset,
  updateAsset,
  deleteAsset,
  exportAssets,
  syncDevicesFromOcs,
  fetchAvailableDevices,
  type CreateAssetRequest,
  type UpdateAssetRequest,
  type AssetFilters,
} from '@/api/assets';

export type { Asset };

/**
 * Hook to fetch all assets with optional filters
 */
export function useAssets(filters?: AssetFilters) {
  return useQuery({
    queryKey: queryKeys.assets.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await fetchAssets(filters);
      return response.assets;
    },
  });
}

/**
 * Hook to fetch a single asset
 */
export function useAsset(id: string) {
  return useQuery({
    queryKey: queryKeys.assets.detail(id),
    queryFn: async () => {
      const response = await fetchAsset(id);
      return response.asset;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch available asset types
 */
export function useAssetTypes() {
  return useQuery({
    queryKey: queryKeys.assets.types(),
    queryFn: async () => {
      const response = await fetchAssetTypes();
      return response.types;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch available asset statuses
 */
export function useAssetStatuses() {
  return useQuery({
    queryKey: queryKeys.assets.statuses(),
    queryFn: async () => {
      const response = await fetchAssetStatuses();
      return response.statuses;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch asset summary statistics
 */
export function useAssetSummary() {
  return useQuery({
    queryKey: queryKeys.assets.summary(),
    queryFn: fetchAssetSummary,
  });
}

/**
 * Hook to create a new asset
 */
export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssetRequest) => {
      return createAsset(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

/**
 * Hook to update an asset
 */
export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAssetRequest }) => {
      return updateAsset(id, data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(id) });
    },
  });
}

/**
 * Hook to delete an asset
 */
export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return deleteAsset(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

/**
 * Hook to export assets to Excel
 */
export function useExportAssets() {
  return useMutation({
    mutationFn: async (filters?: AssetFilters) => {
      const blob = await exportAssets(filters);
      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Hook to sync devices from OCS to assets
 */
export function useSyncDevicesFromOcs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncDevicesFromOcs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

/**
 * Hook to fetch devices available for linking (not yet in assets)
 */
export function useAvailableDevices() {
  return useQuery({
    queryKey: queryKeys.assets.availableDevices(),
    queryFn: async () => {
      const response = await fetchAvailableDevices();
      return response.devices;
    },
  });
}
